"use client";

import { Capacitor } from "@capacitor/core";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  getPushNudgeEligibility,
  recordPushNudgeOutcome,
} from "@/actions/push-nudge";
import type { PushNudgeSurface } from "@/db/schema/push-nudge";
import { usePushNotifications } from "@/hooks/use-push-notifications";
import { captureClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { createClientLogger } from "@/lib/logging/client";
import { openPermissionSettings } from "@/lib/native/permission-settings";
import {
  buildPushNudgeCopy,
  type PushNudgeCopy,
  type PushNudgeMode,
  type PushNudgeParams,
} from "../lib/push-nudge-copy";

const logger = createClientLogger("features/push-nudge/use-push-nudge");

/**
 * Surfaces already counted as seen in this app session.
 *
 * Module-level on purpose: the impression budget belongs to the surface, not to
 * a component instance, and native sessions are long — the app is rarely killed
 * (see project_native_no_reload_state_traps), so "this session" really does
 * mean the whole time the user has had the app open.
 */
const impressionsRecordedThisSession = new Set<string>();

export type PushNudgeStage =
  | "hidden"
  /** An ask that can actually raise the system dialog. */
  | "ask"
  /** The dialog is gone. Only a trip to Settings can undo this. */
  | "rescue"
  /** Granted just now — close the loop before leaving. */
  | "confirmed";

export type PushNudgeView = {
  stage: PushNudgeStage;
  mode: PushNudgeMode;
  copy: PushNudgeCopy;
  isBusy: boolean;
  /** Fires the OS dialog, or opens Settings when in rescue. */
  act: () => Promise<void>;
  dismiss: () => void;
};

const platformLabel = () => {
  const platform = Capacitor.getPlatform();
  if (platform === "ios") return "iOS";
  if (platform === "android") return "Android";
  return "browser";
};

/**
 * Decides whether a surface may ask this user for notification permission, and
 * what shape that ask takes.
 *
 * ## The rule that replaces the spec's cooldown machinery
 *
 * The nudge system holds **one** system dialog per user, for life. iOS shows its
 * alert once per install; Android blocks permanently after two dismissals and
 * onboarding already spends one. So arbitration is not "one nudge per session
 * with a seven-day cooldown" — it is *spend the single shot on the first surface
 * that qualifies*. Once spent, every remaining surface falls through to rescue.
 *
 * Dismissal is deliberately free. "Not now" never reached the OS, so it costs
 * nothing and re-qualifies; only a tap that actually raised the dialog is
 * charged. That asymmetry is the whole reason `prompted` and `dismissed` are
 * separate outcomes in the server action.
 *
 * ## Why it renders nothing off native
 *
 * Web push still works through Settings, but it is not a nudge target: a browser
 * prompt spent badly pushes the whole origin toward Chrome's quieter UI, which
 * is an origin-wide cost paid for one user's ask.
 */
export function usePushNudge(
  surface: PushNudgeSurface,
  params: PushNudgeParams = {},
): PushNudgeView {
  const {
    availability,
    canPromptOs,
    isLoading: pushLoading,
    requestPermission,
  } = usePushNotifications();

  const [eligible, setEligible] = useState<boolean | null>(null);
  const [serverCanPrompt, setServerCanPrompt] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [granted, setGranted] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const impressionRef = useRef(false);

  const isNative = Capacitor.isNativePlatform();

  const mode: PushNudgeMode =
    availability === "provisional" ? "upgrade" : "first_ask";

  const copy = buildPushNudgeCopy(surface, mode, {
    ...params,
    platformLabel: platformLabel(),
  });

  // Resolve the caps once the push layer knows what it is looking at. Asking
  // earlier would spend a round trip on a user who turns out to be `enabled`.
  const relevant =
    isNative &&
    (availability === "provisional" ||
      availability === "can_prompt" ||
      availability === "permission_blocked");

  useEffect(() => {
    if (!relevant) return;
    let cancelled = false;

    void getPushNudgeEligibility(surface).then((result) => {
      if (cancelled) return;
      if (!result.ok) {
        // Bookkeeping is unavailable. A nudge is an optimisation, so the safe
        // failure is to show nothing rather than something uncapped.
        setEligible(false);
        return;
      }
      setEligible(result.data.eligible);
      setServerCanPrompt(result.data.canPrompt);
    });

    return () => {
      cancelled = true;
    };
  }, [relevant, surface]);

  const blocked = availability === "permission_blocked";
  // The device says a dialog can still appear AND the user's budget is unspent.
  // Either one being false means the CTA would resolve to nothing.
  const promptable = canPromptOs && serverCanPrompt;

  const stage: PushNudgeStage = granted
    ? "confirmed"
    : !relevant || dismissed || eligible !== true
      ? "hidden"
      : blocked || !promptable
        ? "rescue"
        : "ask";

  /*
   * One impression per surface per app session — not per mount.
   *
   * Per mount was wrong, and expensively so: with a lifetime cap of two, a user
   * who opened the ticket screen, went back, and opened it again had spent the
   * entire budget for that surface without ever reading the card. Remounts are
   * normal (back-navigation, a tab returning to the foreground, React
   * re-mounting a subtree), and none of them are a new chance to persuade
   * anyone.
   *
   * The guard is module-level rather than a ref because a ref only dedupes
   * within one hook instance, and two components can legitimately mount the
   * same surface at once — each would otherwise charge an impression for the
   * same pixels.
   */
  useEffect(() => {
    if (stage !== "ask" && stage !== "rescue") return;
    if (impressionRef.current) return;
    if (impressionsRecordedThisSession.has(surface)) return;
    impressionRef.current = true;
    impressionsRecordedThisSession.add(surface);

    captureClientEvent(ANALYTICS_EVENTS.PUSH_NUDGE_SHOWN, {
      surface,
      variant: stage,
      mode,
    });
    void recordPushNudgeOutcome(surface, "shown");
  }, [stage, surface, mode]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    captureClientEvent(ANALYTICS_EVENTS.PUSH_NUDGE_DISMISSED, {
      surface,
      variant: stage,
      mode,
    });
    void recordPushNudgeOutcome(surface, "dismissed");
  }, [surface, stage, mode]);

  const act = useCallback(async () => {
    captureClientEvent(ANALYTICS_EVENTS.PUSH_NUDGE_CTA_TAPPED, {
      surface,
      variant: stage,
      mode,
    });

    if (stage === "rescue") {
      await openPermissionSettings("notifications");
      setDismissed(true);
      return;
    }

    setIsBusy(true);
    try {
      // The budget is charged here, not on render and not on tap-intent: this
      // is the first line that can actually put a dialog on screen. Charging
      // earlier would bill a user who backed out before it appeared.
      captureClientEvent(ANALYTICS_EVENTS.PUSH_NUDGE_PROMPT_FIRED, {
        surface,
        mode,
      });
      await recordPushNudgeOutcome(surface, "prompted");

      await requestPermission();

      captureClientEvent(ANALYTICS_EVENTS.PUSH_NUDGE_GRANTED, {
        surface,
        mode,
      });
      await recordPushNudgeOutcome(surface, "granted");
      setGranted(true);
    } catch (error) {
      logger.warn("Push nudge permission request failed", error);
      captureClientEvent(ANALYTICS_EVENTS.PUSH_NUDGE_DENIED, {
        surface,
        mode,
      });
      await recordPushNudgeOutcome(surface, "denied");
      // The underlying screen is left exactly as it was. A booking that just
      // succeeded must not look like it failed because a permission did.
      setDismissed(true);
    } finally {
      setIsBusy(false);
    }
  }, [stage, surface, mode, requestPermission]);

  return {
    stage,
    mode,
    copy,
    isBusy: isBusy || pushLoading,
    act,
    dismiss,
  };
}
